/**
 * Ring Configurator - Image Compositing Engine
 * Komponerar ring-bilder baserat på användarens val
 */

class RingConfigurator {
  constructor(containerId, options = {}) {
    this.container = document.getElementById(containerId);
    if (!this.container) {
      console.warn('RingConfigurator: Container not found');
      return;
    }

    this.sectionId = options.sectionId || '';
    this.productHandle = options.productHandle || '';
    this.baseImage = options.baseImage || '';
    this.addonVariantMap = options.addonVariantMap || {};
    this.currency = 'SEK';
    
    this.canvas = this.container.querySelector(`#ring-preview-canvas-${this.sectionId}`);
    this.loadingEl = this.container.querySelector(`#ring-preview-loading-${this.sectionId}`);
    this.summaryList = this.container.querySelector(`#ring-summary-list-${this.sectionId}`);
    this.priceEl = this.container.querySelector(`#ring-price-${this.sectionId}`);
    this.resetBtn = this.container.querySelector(`#ring-reset-${this.sectionId}`);
    this.downloadBtn = this.container.querySelector(`#ring-download-${this.sectionId}`);
    this.addToCartBtn = this.container.querySelector(`#ring-add-to-cart-${this.sectionId}`);

    this.ctx = this.canvas ? this.canvas.getContext('2d') : null;
    
    // Current configuration
    this.config = {
      model: null,
      color: null,
      edges: null,
      surface: null,
      inlay: null
    };

    // Pricing state (cents)
    this.basePriceCents = null;
    this.totalDeltaCents = 0;
    this.selectedAddonVariantId = null;
    this.selectedAddonAmountCents = 0;

    // Image cache
    this.imageCache = new Map();
    
    // Initialize
    this.init();
  }

  init() {
    if (!this.canvas || !this.ctx) {
      console.error('RingConfigurator: Canvas not available');
      return;
    }

    // Set canvas size
    this.resizeCanvas();

    // Event listeners
    this.setupEventListeners();

    // Load initial configuration
    this.loadInitialConfig();

    // Pricing
    this.loadBasePrice();
    this.updateSummary();
    this.updatePriceState();

    // Initial render
    this.render();
  }

  resizeCanvas() {
    const container = this.canvas.parentElement;
    const size = Math.min(container.clientWidth, container.clientHeight);
    this.canvas.width = size;
    this.canvas.height = size;
  }

  setupEventListeners() {
    // Option buttons
    const optionButtons = this.container.querySelectorAll('.ring-configurator__option');
    optionButtons.forEach(btn => {
      btn.addEventListener('click', (e) => {
        const optionType = btn.closest('[data-option]')?.dataset.option;
        const value = btn.dataset.value;
        const image = btn.dataset.image;

        if (optionType) {
          this.selectOption(optionType, value, image, btn);
        }
      });
    });

    // Reset button
    if (this.resetBtn) {
      this.resetBtn.addEventListener('click', () => {
        this.reset();
      });
    }

    // Download button
    if (this.downloadBtn) {
      this.downloadBtn.addEventListener('click', () => {
        this.downloadImage();
      });
    }

    // Add to cart button
    if (this.addToCartBtn) {
      this.addToCartBtn.addEventListener('click', () => {
        this.addToCart();
      });
    }

    // Window resize
    window.addEventListener('resize', () => {
      this.resizeCanvas();
      this.render();
    });
  }

  downloadImage() {
    if (!this.canvas) return;
    try {
      // Use PNG for lossless result
      const dataUrl = this.canvas.toDataURL('image/png');
      const link = document.createElement('a');
      link.href = dataUrl;
      link.download = `ring-configurator-${this.sectionId}.png`;
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
    } catch (error) {
      console.warn('RingConfigurator: download failed', error);
    }
  }

  loadInitialConfig() {
    // Get first selected option for each type
    const optionGroups = this.container.querySelectorAll('[data-option]');
    optionGroups.forEach(group => {
      const optionType = group.dataset.option;
      const firstSelected = group.querySelector('.is-selected') || group.querySelector('.ring-configurator__option:not(.is-disabled)');
      
      if (firstSelected) {
        this.config[optionType] = {
          value: firstSelected.dataset.value,
          image: firstSelected.dataset.image,
          priceDeltaCents: this.getPriceDeltaCents(firstSelected)
        };
      }
    });

    // After loading config, update edges visibility based on initial model
    if (this.config.model?.value) {
      this.updateEdgesVisibility(this.config.model.value);
    }
  }

  selectOption(optionType, value, image, buttonElement) {
    // Update config
    this.config[optionType] = {
      value: value,
      image: image,
      priceDeltaCents: this.getPriceDeltaCents(buttonElement)
    };

    // Update UI - remove selected from siblings
    const optionGroup = buttonElement.closest('[data-option]');
    optionGroup.querySelectorAll('.is-selected').forEach(el => {
      el.classList.remove('is-selected');
    });
    buttonElement.classList.add('is-selected');

    // Handle dependencies when model changes
    if (optionType === 'model') {
      this.updateEdgesVisibility(value);
    }

    // Update summary
    this.updateSummary();

    // Update price
    this.updatePriceState();

    // Re-render
    this.render();
  }

  async render() {
    if (!this.ctx) return;

    this.showLoading();

    try {
      // Clear canvas
      this.ctx.clearRect(0, 0, this.canvas.width, this.canvas.height);

      // Determine which image to use
      // Draw order:
      // 1) Model (base)
      // 2) Edges overlay (if model is "Med kant")
      // 3) Surface overlay
      // 4) Color adjustment (tint/desaturate) - applies to everything drawn so far
      // 5) Inlay overlay (not tinted)

      // Model (base)
      const modelImage = this.config.model?.image || this.baseImage;
      if (modelImage) {
        const modelImg = await this.loadImage(modelImage);
        if (modelImg) {
          this.drawImageCentered(modelImg);
        }
      }

      // Edges overlay (only if model is "Med kant")
      if (this.config.model?.value && this.isModelWithEdges(this.config.model.value)) {
        if (this.config.edges?.image) {
          const edgesImg = await this.loadImage(this.config.edges.image);
          if (edgesImg) {
            this.drawImageCentered(edgesImg);
          }
        }
      }

      // Surface overlay
      if (this.config.surface?.image) {
        const surfaceImg = await this.loadImage(this.config.surface.image);
        if (surfaceImg) {
          this.drawImageCentered(surfaceImg);
        }
      }

      // Color adjustment (tint/desaturate) - applies to everything except inlay
      if (this.config.color?.value) {
        this.applyColorAdjustment(this.config.color.value);
      }

      // Inlay (not affected by color filter)
      if (this.config.inlay?.image && this.config.inlay.value !== 'none') {
        const inlayImg = await this.loadImage(this.config.inlay.image);
        if (inlayImg) {
          this.drawImageCentered(inlayImg);
        }
      }

    } catch (error) {
      console.error('RingConfigurator: Render error', error);
    } finally {
      this.hideLoading();
    }
  }

  async loadImage(src) {
    if (!src) return null;

    // Check cache
    if (this.imageCache.has(src)) {
      return this.imageCache.get(src);
    }

    return new Promise((resolve) => {
      const img = new Image();
      
      // Try with CORS first (for cross-origin images)
      // Shopify CDN images should work with CORS
      img.crossOrigin = 'anonymous';
      
      let corsFailed = false;
      
      img.onload = () => {
        this.imageCache.set(src, img);
        resolve(img);
      };
      
      img.onerror = (error) => {
        // If CORS fails, try without CORS (works for same-origin images)
        if (!corsFailed && src.includes('cdn.shopify.com')) {
          corsFailed = true;
          const imgRetry = new Image();
          imgRetry.onload = () => {
            this.imageCache.set(src, imgRetry);
            resolve(imgRetry);
          };
          imgRetry.onerror = () => {
            console.warn('RingConfigurator: Failed to load image', src);
            resolve(null);
          };
          // Try without CORS
          imgRetry.crossOrigin = null;
          imgRetry.src = src;
        } else {
          console.warn('RingConfigurator: Failed to load image', src, error);
          resolve(null);
        }
      };
      
      img.src = src;
    });
  }

  drawImageCentered(img) {
    if (!img || !this.ctx) return;

    try {
      const canvas = this.canvas;
      const scale = Math.min(
        canvas.width / img.width,
        canvas.height / img.height
      );

      const scaledWidth = img.width * scale;
      const scaledHeight = img.height * scale;
      const x = (canvas.width - scaledWidth) / 2;
      const y = (canvas.height - scaledHeight) / 2;

      this.ctx.drawImage(img, x, y, scaledWidth, scaledHeight);
    } catch (error) {
      // CORS error or other canvas issue
      console.warn('RingConfigurator: Canvas draw error (may be CORS related)', error);
      
      // Fallback: Show image as <img> element instead of canvas
      this.fallbackToImage(img);
    }
  }

  fallbackToImage(img) {
    // If canvas fails (CORS), show image directly
    const container = this.canvas.parentElement;
    let fallbackImg = container.querySelector('.ring-configurator__fallback-image');
    
    if (!fallbackImg) {
      fallbackImg = document.createElement('img');
      fallbackImg.className = 'ring-configurator__fallback-image';
      fallbackImg.style.cssText = 'width: 100%; height: 100%; object-fit: contain; position: absolute; top: 0; left: 0;';
      container.style.position = 'relative';
      container.appendChild(fallbackImg);
    }
    
    fallbackImg.src = img.src;
    this.canvas.style.display = 'none';
  }


  isModelWithEdges(modelValue) {
    // "Med kant" model requires edges option
    const modelStr = String(modelValue).toLowerCase().trim();
    return modelStr === 'med kant' || modelStr === 'med-kant' || modelStr === 'medkant';
  }

  updateEdgesVisibility(modelValue) {
    const edgesGroup = this.container.querySelector('.ring-configurator__control-group--edges');
    if (!edgesGroup) return;

    const shouldShow = this.isModelWithEdges(modelValue);
    
    if (shouldShow) {
      edgesGroup.style.display = '';
      // Select first edges option if none selected
      const edgesOptions = edgesGroup.querySelector('[data-option="edges"]');
      if (edgesOptions) {
        const selected = edgesOptions.querySelector('.is-selected');
        if (!selected) {
          const firstOption = edgesOptions.querySelector('.ring-configurator__option:not(.is-disabled)');
          if (firstOption) {
            firstOption.click();
          }
        }
      }
    } else {
      edgesGroup.style.display = 'none';
      // Reset edges config when hidden
      this.config.edges = null;
    }
  }

  updateSummary() {
    if (!this.summaryList) return;

    this.summaryList.innerHTML = '';

    Object.entries(this.config).forEach(([key, config]) => {
      if (config && config.value && config.value !== 'none') {
        const li = document.createElement('li');
        
        // Get label from button
        const optionGroup = this.container.querySelector(`[data-option="${key}"]`);
        const selectedBtn = optionGroup?.querySelector('.is-selected');
        const label = selectedBtn?.textContent?.trim() || key;
        
        li.textContent = `${this.getOptionLabel(key)}: ${label}`;
        this.summaryList.appendChild(li);
      }
    });
  }

  applyColorAdjustment(colorValue) {
    if (!this.ctx) return;
    const hex = this.normalizeHex(colorValue);
    if (!hex) return;

    // Gold: no change
    if (hex === '#d6b258') {
      return;
    }

    // Silver: desaturate (saturation 0)
    if (hex === '#c0c0c0') {
      this.desaturateCanvas();
      return;
    }

    // Other colors: tint overlay
    const { r, g, b } = this.hexToRgb(hex) || {};
    if (r === undefined) return;

    this.ctx.save();
    this.ctx.globalCompositeOperation = 'source-atop';
    this.ctx.fillStyle = `rgba(${r}, ${g}, ${b}, 0.7)`;
    this.ctx.fillRect(0, 0, this.canvas.width, this.canvas.height);
    this.ctx.restore();
  }

  hexToRgb(hex) {
    if (!hex) return null;
    let h = hex.replace('#', '');
    if (h.length === 3) {
      h = h.split('').map(c => c + c).join('');
    }
    const int = parseInt(h, 16);
    if (Number.isNaN(int)) return null;
    return {
      r: (int >> 16) & 255,
      g: (int >> 8) & 255,
      b: int & 255
    };
  }

  normalizeHex(hex) {
    if (!hex) return null;
    let h = hex.trim().toLowerCase();
    if (!h.startsWith('#')) {
      h = `#${h}`;
    }
    if (h.length === 4) {
      h = `#${h[1]}${h[1]}${h[2]}${h[2]}${h[3]}${h[3]}`;
    }
    if (h.length !== 7) return null;
    return h;
  }

  desaturateCanvas() {
    if (!this.ctx || !this.canvas) return;
    try {
      const { width, height } = this.canvas;
      const imageData = this.ctx.getImageData(0, 0, width, height);
      const data = imageData.data;
      for (let i = 0; i < data.length; i += 4) {
        const r = data[i];
        const g = data[i + 1];
        const b = data[i + 2];
        // Luma (Rec. 709)
        const gray = 0.2126 * r + 0.7152 * g + 0.0722 * b;
        data[i] = data[i + 1] = data[i + 2] = gray;
      }
      this.ctx.putImageData(imageData, 0, 0);
    } catch (error) {
      console.warn('RingConfigurator: desaturate failed', error);
    }
  }

  getOptionLabel(key) {
    const labels = {
      model: 'Modell',
      color: 'Färg',
      edges: 'Kanter',
      surface: 'Yta',
      inlay: 'Inlägg'
    };
    return labels[key] || key;
  }

  getPriceDeltaCents(buttonEl) {
    if (!buttonEl) return 0;
    const raw = buttonEl.dataset?.price;
    const num = Number.parseFloat(raw);
    if (!Number.isFinite(num)) return 0;
    return Math.round(num * 100);
  }

  formatPrice(cents) {
    const amount = (cents || 0) / 100;
    return new Intl.NumberFormat('sv-SE', { style: 'currency', currency: this.currency }).format(amount);
  }

  computeTotalDeltaCents() {
    return Object.values(this.config).reduce((sum, cfg) => {
      const delta = cfg?.priceDeltaCents;
      if (typeof delta === 'number' && Number.isFinite(delta)) {
        return sum + delta;
      }
      return sum;
    }, 0);
  }

  computeAddonVariant(totalDeltaCents) {
    if (!totalDeltaCents || totalDeltaCents <= 0) return { variantId: null, amountCents: 0 };
    const entries = Object.entries(this.addonVariantMap || {})
      .map(([amount, variantId]) => {
        const parsed = Number.parseFloat(amount);
        if (!Number.isFinite(parsed)) return null;
        return { amountCents: Math.round(parsed * 100), variantId };
      })
      .filter(Boolean)
      .sort((a, b) => b.amountCents - a.amountCents);

    const maxDiffCents = 50 * 100;
    for (const entry of entries) {
      if (entry.amountCents <= totalDeltaCents) {
        const diff = totalDeltaCents - entry.amountCents;
        if (diff <= maxDiffCents) {
          return entry;
        }
      }
    }
    return null;
  }

  updatePriceState() {
    this.totalDeltaCents = this.computeTotalDeltaCents();

    const hasAddonMap = this.addonVariantMap && Object.keys(this.addonVariantMap).length > 0;
    let allowed = true;
    let reason = '';
    let addonVariantId = null;
    let addonAmountCents = 0;

    if (this.totalDeltaCents > 0) {
      if (!hasAddonMap) {
        allowed = false;
        reason = 'Ingen add-on-karta är konfigurerad.';
      } else {
        const match = this.computeAddonVariant(this.totalDeltaCents);
        if (match) {
          addonVariantId = match.variantId;
          addonAmountCents = match.amountCents;
        } else {
          allowed = false;
          reason = 'Ingen add-on inom 50 kr från tillägget.';
        }
      }
    }

    this.selectedAddonVariantId = addonVariantId;
    this.selectedAddonAmountCents = addonAmountCents;
    this.addonState = { allowed, reason };

    if (this.addToCartBtn) {
      const baseDisabled = !this.productHandle;
      this.addToCartBtn.disabled = baseDisabled || !allowed;
    }

    this.updatePriceDisplay();
  }

  updatePriceDisplay() {
    if (!this.priceEl) return;

    const textTarget = this.priceEl.querySelector('.ring-configurator__price-text');
    if (!textTarget) return;

    if (!this.basePriceCents) {
      textTarget.textContent = 'Baspris laddas...';
      return;
    }

    const addOnCents = this.selectedAddonAmountCents || 0;
    const total = this.basePriceCents + addOnCents;

    if (!this.addonState?.allowed && this.totalDeltaCents > 0) {
      const addText = this.formatPrice(this.totalDeltaCents);
      textTarget.textContent = `${this.addonState.reason} (Tillägg: ${addText})`;
      return;
    }

    textTarget.textContent = `Baspris ${this.formatPrice(this.basePriceCents)} + tillägg ${this.formatPrice(addOnCents)} = ${this.formatPrice(total)}`;
  }

  async loadBasePrice() {
    if (!this.productHandle) {
      return;
    }
    try {
      const productResponse = await fetch(`/products/${this.productHandle}.js`);
      if (!productResponse.ok) return;
      const product = await productResponse.json();
      const variant = product.variants.find(v => v.available) || product.variants[0];
      if (variant) {
        this.basePriceCents = variant.price;
        this.updatePriceDisplay();
      }
    } catch (error) {
      console.warn('RingConfigurator: Failed to load base price', error);
    }
  }

  reset() {
    // Reset to first option in each group
    const optionGroups = this.container.querySelectorAll('[data-option]');
    optionGroups.forEach(group => {
      const firstOption = group.querySelector('.ring-configurator__option');
      if (firstOption) {
        firstOption.click();
      }
    });
  }

  async addToCart() {
    if (!this.productHandle) {
      alert('Produkt är inte konfigurerad. Kontakta support.');
      return;
    }

    // Ensure pricing state is current
    this.updatePriceState();

    // Block if addon rule fails
    if (this.totalDeltaCents > 0 && this.addonState && !this.addonState.allowed) {
      alert(this.addonState.reason || 'Kan inte lägga till tillägg inom gränsen 50 kr.');
      return;
    }

    // Get product data
    try {
      const productResponse = await fetch(`/products/${this.productHandle}.js`);
      if (!productResponse.ok) {
        throw new Error('Product not found');
      }

      const product = await productResponse.json();
      
      // Get first available variant (or fallback to first)
      const variant = product.variants.find(v => v.available) || product.variants[0];
      if (!variant) {
        throw new Error('No variants available');
      }

      // Prepare cart data with configuration
      const properties = {};
      Object.entries(this.config).forEach(([key, config]) => {
        if (config && config.value && config.value !== 'none') {
          const optionGroup = this.container.querySelector(`[data-option="${key}"]`);
          const selectedBtn = optionGroup?.querySelector('.is-selected');
          const label = selectedBtn?.textContent?.trim() || config.value;
          
          properties[`Ring ${this.getOptionLabel(key)}`] = label;
        }
      });

      // Price transparency
      properties['Ring Plus tillägg (kr)'] = (this.selectedAddonAmountCents / 100).toFixed(2);

      // Add base first, then add-on (separate requests to avoid 422 on batch)
      await this.addSingleItemToCart(variant.id, 1, properties);

      if (this.selectedAddonVariantId && this.selectedAddonAmountCents > 0) {
        await this.addSingleItemToCart(this.selectedAddonVariantId, 1, {});
      }

      // Trigger cart update event
      document.dispatchEvent(new CustomEvent('cart:change'));

      // Show success message
      alert('Ringen har lagts till i varukorgen!');

    } catch (error) {
      console.error('RingConfigurator: Add to cart error', error);
      alert('Ett fel uppstod. Försök igen.');
    }
  }

  async addSingleItemToCart(variantId, quantity = 1, properties = {}) {
    const formData = new FormData();
    formData.append('id', variantId);
    formData.append('quantity', quantity);

    Object.entries(properties || {}).forEach(([key, value]) => {
      if (value !== undefined && value !== null && value !== '') {
        formData.append(`properties[${key}]`, value);
      }
    });

    const resp = await fetch('/cart/add.js', {
      method: 'POST',
      body: formData,
      headers: {
        'Accept': 'application/json'
      }
    });

    if (!resp.ok) {
      let msg = 'Failed to add to cart';
      try {
        const data = await resp.json();
        if (data?.description) msg = data.description;
        if (data?.message) msg = `${msg}: ${data.message}`;
      } catch (e) {
        // ignore parse errors
      }
      throw new Error(msg);
    }
  }

  showLoading() {
    if (this.loadingEl) {
      this.loadingEl.classList.add('is-loading');
    }
  }

  hideLoading() {
    if (this.loadingEl) {
      this.loadingEl.classList.remove('is-loading');
    }
  }
}

// Export for global use
window.RingConfigurator = RingConfigurator;



